/* Domovoi · PostHog analytics, loaded on every page.

   One file so the site has one place that decides what gets tracked.
   Loads the PostHog snippet async (no render cost), captures pageviews,
   pageleaves and autocapture clicks, records sessions, and identifies the
   signed-in person by their Supabase user id so web, app and database
   data all key off the same id. Exposes window.DVTrack(name, props) for
   product events — call it from Catered/Considered/Curated, don't init
   PostHog anywhere else.

   Project token is a public, write-only key; it is meant to be in the page. */
/* global posthog -- window.posthog, created by the snippet below */
import { ORIGIN, POSTHOG_TOKEN } from './config.js';
import { platform, shellVersion, isProductionHost, isNative, BUNDLE_SHA } from './platform.js';

(function () {
  var TOKEN = POSTHOG_TOKEN;
  // Sent through our own domain (vercel.json rewrites /ingest to PostHog) so
  // ad and tracker blockers don't silently drop events. Absolute, because
  // inside the app shell the page's own origin is not domovoi.ca.
  var HOST  = ORIGIN + '/ingest';

  // Skip local previews and Vercel preview deploys so dev traffic stays out.
  // The app's pages are served from localhost, so the hostname can't tell us;
  // every app build is a real one (TestFlight testers are test accounts).
  var isProd = isProductionHost() || isNative();

  /* Official async snippet (posthog-js). */
  !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionManager persistence sessionPropsManager sessionPersistence captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing is_capturing getPageViewId captureTraceFeedback captureTraceMetric".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);

  /* Never send credentials. Supabase's OAuth and magic-link redirects put the
     session in the URL fragment (#access_token=…&refresh_token=…&provider_token=…),
     and PostHog copies the URL into $current_url, $pathname-adjacent props and
     person properties. Redact token-like params from every string property, and
     don't record the replay of a page that arrived with tokens in its URL. */
  var SECRET_RE = /([#?&])(access_token|refresh_token|provider_token|provider_refresh_token|id_token|token_hash|token|code)=[^&#]*/gi;
  function scrub(v) { return typeof v === 'string' && v.indexOf('=') > -1 ? v.replace(SECRET_RE, '$1$2=redacted') : v; }
  function scrubAll(o) { if (o && typeof o === 'object') for (var k in o) o[k] = scrub(o[k]); }
  var tokenInUrl = /(access|refresh|provider)_token=/.test(window.location.hash + window.location.search);

  posthog.init(TOKEN, {
    before_send: function (ev) {
      if (!ev) return ev;
      scrubAll(ev.properties); scrubAll(ev.$set); scrubAll(ev.$set_once);
      if (ev.properties) { scrubAll(ev.properties.$set); scrubAll(ev.properties.$set_once); }
      return ev;
    },
    disable_session_recording: tokenInUrl || platform() !== 'web',   // never inside the app (the mobile plan)
    api_host: HOST,
    ui_host: 'https://us.posthog.com',
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: true,
    person_profiles: 'identified_only',
    capture_exceptions: true,   // error tracking: unhandled errors + rejections
    session_recording: { maskAllInputs: true },
    opt_out_capturing_by_default: !isProd
  });

  /* Every event says where it came from, so web and app can be compared and a
     regression traced to the build that shipped it. */
  posthog.register({ platform: platform(), bundle_sha: BUNDLE_SHA, shell_version: shellVersion() });

  /* Tie the browser to the Supabase user. identity.js is loaded before this
     file on every page that has an account; tools pages have no sign-in. */
  function identify() {
    var ID = window.DVIdentity;
    if (!ID) return;
    var s = ID.session();
    if (!s || !s.access_token) return;
    try {
      var body = JSON.parse(atob(s.access_token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (!body.sub) return;
      var props = {};
      if (body.email) props.email = body.email;
      var cached = ID.read && ID.read();
      if (cached && cached.name) props.name = cached.name;
      if (cached && 'partner' in cached) props.has_partner = !!cached.partner;
      posthog.identify(body.sub, props);
    } catch (e) {}
  }
  identify();

  /* Call right after sign-up / sign-in, before the next page load, so the
     first product events already carry the user id. */
  window.DVIdentify = function (userId, props) {
    try { if (userId) posthog.identify(userId, props || {}); } catch (e) {}
  };

  /* Caught errors you still want to see: DVTrackError(err, { where: 'catered.sync' }) */
  window.DVTrackError = function (err, props) {
    try { posthog.captureException(err, props || {}); } catch (e) {}
  };

  /* Product events. DVTrack('recipe_saved', { recipe_id: ... }) */
  window.DVTrack = function (name, props) {
    try { posthog.capture(name, props || {}); } catch (e) {}
  };
  /* Call on sign-out so the next visitor on this browser isn't merged in. */
  window.DVTrackReset = function () {
    try { posthog.reset(); } catch (e) {}
  };
})();
